import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Button, Divider, Space, Typography } from 'antd';
import { MenuItem } from '~/types/Menu';
import i18n from '~/i18n';
import InputQuantity from '~/App/components/InputQuantity';
import './style.css';
import { formatCurrency } from '~/utils/currency';
import TextArea from 'antd/lib/input/TextArea';
import { CartItem } from '~/types/Cart';
import { CartPanelMode } from './constants';
import { webImageUrl } from '../../utils/url';
import { useMenuAvailabilityMap } from '../hooks/useMenuAvailability';
import { MenuAvailabilityType } from '@restaurant/types';
import { CartStateContext } from '../Layout/providers/CartProvider';

const CartPanel = ({
  mode = CartPanelMode.Add,
  item,
  initialValues,
  index,
  onSubmit,
}: {
  mode?: CartPanelMode;
  item: MenuItem;
  initialValues?: CartItem;
  index?: number;
  onSubmit: (item: CartItem) => void;
}) => {
  const availabilityMap = useMenuAvailabilityMap();
  const cart = useContext(CartStateContext);

  const getMaxQuantity = useCallback(
    (menuId: string) => {
      const availability = availabilityMap[menuId];
      if (!availability) return undefined;

      const { availabilityType, stockCounter } = availability;
      if (availabilityType === MenuAvailabilityType.ToggleOff) return 0;

      // Count all items in the cart of the same menu
      // if cart edit mode, exclude the currently edited item (by index, item already in cart wil have index)
      const inCartCount = cart.reduce((acc, cur, currentIndex) => {
        if (currentIndex !== index) {
          if (cur.menuId === menuId) {
            acc += cur.quantity;
          }

          cur.addOns.forEach((item) => {
            if (item.menuId === menuId) {
              acc += item.quantity;
            }
          });
        }

        return acc;
      }, 0);

      return Math.max((stockCounter?.current ?? 0) - inCartCount, 0);
    },
    [availabilityMap, cart, index]
  );

  const mainItemMaxQuantity = useMemo(() => {
    return getMaxQuantity(item.id);
  }, [getMaxQuantity, item.id]);

  const [quantity, setQuantity] = useState<number>(() => {
    if (initialValues?.quantity) {
      return initialValues?.quantity;
    }

    return mainItemMaxQuantity != null ? Math.min(1, mainItemMaxQuantity) : 1;
  });

  const [addOnsQuantity, setAddOnsQuantity] = useState<number[]>(() => {
    if (initialValues?.custom) {
      return [];
    }

    let index = 0;
    return initialValues?.addOns
      ? item.addOns.map(({ id }) => {
          const currentAddOn = initialValues.addOns[index];
          if (currentAddOn?.menuId === id) {
            index++;
            return currentAddOn.quantity;
          } else {
            return 0;
          }
        })
      : item.addOns.map(() => 0);
  });
  const [notes, setNotes] = useState<string>(initialValues?.notes ?? '');

  const onChangeQuantity = (value: number | null) => {
    let newQuantity = Math.max(value ?? 0, mode === CartPanelMode.Add ? 1 : 0);
    if (mainItemMaxQuantity != null) {
      newQuantity = Math.min(newQuantity, mainItemMaxQuantity);
    }
    setQuantity(newQuantity);
  };

  const onChangeAddOnQuantity = (value: number | null, index: number) => {
    let newVal = value || 0;

    const addOnMaxQuantity = getMaxQuantity(item.addOns[index].id);
    if (addOnMaxQuantity != null) {
      newVal = Math.min(newVal, addOnMaxQuantity);
    }

    const newAddOnsQuantity = [...addOnsQuantity];
    newAddOnsQuantity[index] = newVal;

    setAddOnsQuantity(newAddOnsQuantity);
  };

  const onChangeNotes = (e: any) => {
    setNotes(e.target.value);
  };

  const handleSubmit = () => {
    onSubmit({
      menuId: item?.id,
      quantity,
      addOns: addOnsQuantity
        .map((q, index) => {
          return q
            ? {
                menuId: item.addOns[index].id,
                quantity: q,
              }
            : null;
        })
        .filter((val) => val != null) as CartItem['addOns'],
      notes,
      custom: initialValues?.custom ?? false,
      name: initialValues?.name ?? '',
      totalPrice: initialValues?.totalPrice ?? 0,
    });
  };

  const handleDelete = () => {
    onSubmit({
      menuId: item?.id,
      quantity: 0,
      addOns: [],
      notes,
      custom: false,
      name: '',
      totalPrice: 0,
    });
  };

  const paddingBottom =
    mode === CartPanelMode.Edit ? 'pb-[100px]' : ' pb-[64px]';

  let image, name, price;
  if (initialValues?.custom) {
    image = null;
    name = initialValues.name;
    price = initialValues.totalPrice;
  } else {
    image = <img src={webImageUrl(item.image)} className="menu-panel-image" />;
    name = item.name;
    price = item.price;
  }

  const getIsPlusDisabled = useCallback(
    (menuId: string, q: number) => {
      // custom item cannot set quantity
      if (initialValues?.custom) return true;

      const maxQuantity = getMaxQuantity(menuId);
      if (maxQuantity != null) {
        return q >= maxQuantity;
      }
      return false;
    },
    [initialValues?.custom, getMaxQuantity]
  );

  const isSubmitDisabled = (() => {
    if (mode === CartPanelMode.Add) {
      return quantity <= 0;
    }
    return false;
  })();

  return (
    <div className={`menu-panel-container relative ${paddingBottom}`}>
      <div style={{ marginBottom: '16px' }}>
        <div className="d-flex">
          {/* Only show image if item is not a custom. */}
          {/* {image} */}
          <div style={{ flex: 1 }}>
            <p className="text-2xl font-semibold mb-1">{name}</p>
            <p className="mb-0">{formatCurrency(price)}</p>
          </div>
          <InputQuantity
            value={quantity}
            onChange={onChangeQuantity}
            isPlusDisabled={getIsPlusDisabled(item.id, quantity)}
          />
        </div>
        <Divider />
        <div>
          <Typography.Title level={4}>
            {i18n.t('menu_panel_add_ons')}
          </Typography.Title>
          {/* Only show add-ons if item is not a custom */}
          {!initialValues?.custom &&
            item.addOns?.map((addOn, index) => (
              <div key={addOn.id}>
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <p className="mb-0" style={{ fontWeight: 500 }}>
                      {addOn.name}
                    </p>
                    <p className="mb-0">{formatCurrency(addOn.price)}</p>
                  </div>
                  <InputQuantity
                    value={addOnsQuantity[index] ?? 0}
                    onChange={(value) => onChangeAddOnQuantity(value, index)}
                    isPlusDisabled={getIsPlusDisabled(
                      addOn.id,
                      addOnsQuantity[index] ?? 0
                    )}
                  />
                </div>
                <Divider style={{ margin: '8px 0' }} />
              </div>
            ))}
        </div>
        <p className="mb-0">{i18n.t('menu_panel_notes')}</p>
        <TextArea rows={2} value={notes} onChange={onChangeNotes} />
      </div>
      <Space
        direction="vertical"
        style={{ left: 24, right: 24, position: 'fixed', bottom: 24 }}
      >
        <Button
          size="large"
          block
          onClick={handleSubmit}
          type="primary"
          disabled={isSubmitDisabled}
        >
          {i18n.t('menu_panel_submit')}
        </Button>
        {mode === CartPanelMode.Edit ? (
          <Button size="large" block danger onClick={handleDelete}>
            Hapus
          </Button>
        ) : null}
      </Space>
    </div>
  );
};

export default CartPanel;
